// src/components/AdminDashboard.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  TableRow,
  TablePagination,
  IconButton as MuiIconButton,
} from "@mui/material";
import {
  Button,
  Input,
  Select,
  Table,
  Dialog,
  Menu,
  Chip,
  PageHeader,
  PageBlockTitle,
  PageShell,
  Card,
  CardContent,
  TableActionsMenu,
  TableActionsMenuButton,
  destructiveMenuItemSx,
  stopTableRowClick,
} from "../ui";
import { acme, acmePageSectionCardSx, acmeBillingSectionContentSx } from "../../theme/acmeTheme";
import PageSnackbars from "../common/PageSnackbars";
import { usePageSnackbar } from "../common/usePageSnackbar";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import AvatarWithStatus from "../AvatarWithStatus";
import AddIcon from "@mui/icons-material/Add";
import SwitchAccountIcon from "@mui/icons-material/SwitchAccount";
import UserSelector from "../UserSelector/UserSelector";
import { useOutletContext } from "react-router-dom";
import { PLAN_LIMITS } from "../../config/billingPlans";
import { useUserStatuses } from "../../SocketContext";
import {
  isAdminOrManagerRole,
  normalizeRole,
  ROLE_ADMIN,
  ROLE_USER,
  ROLE_MANAGER,
  ROLE_SUPPORT,
} from "../../utils/userRoles";

// *** ПЛАНЫ ДЛЯ АДМИНА (БЕЗ ЦЕН) ***
const availablePlans = Object.keys(PLAN_LIMITS).map((name) => ({
  name,
  limit: PLAN_LIMITS[name],
}));

const AdminDashboard = () => {
  const { user: currentUser, onUserChange } = useOutletContext();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoadDone = useRef(false);
  const userStatuses = useUserStatuses();
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    login: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "",
    plan: "",
  });

  // ??????? ??? ???????? (????? Avatar ? Actions)
  const [filters, setFilters] = useState({
    login: "",
    email: "",
    role: "",
    plan: "",
    status: "",
    createdAt: "",
  });

  // ?????????
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ????????? ??? ????????
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [newPlan, setNewPlan] = useState("");

  // ???? ????????
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  // ????????? ?????????????
  const [loginError, setLoginError] = useState("");
  const [emailError, setEmailError] = useState("");

  const { setSuccessMessage, setErrorMessage, snackbarProps } =
    usePageSnackbar();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [switchUserDialogOpen, setSwitchUserDialogOpen] = useState(false);

  // Состояния для отправки email
  const [sendMailDialogOpen, setSendMailDialogOpen] = useState(false);
  const [mailRecipient, setMailRecipient] = useState("");
  const [mailSubject, setMailSubject] = useState("");
  const [mailBody, setMailBody] = useState("");
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [mailFrom, setMailFrom] = useState("");
  const [sendingMail, setSendingMail] = useState(false);

  // Уникальные значения для фильтров
  const [uniqueRoles, setUniqueRoles] = useState([]);
  const [uniquePlans, setUniquePlans] = useState([]);
  const [uniqueStatuses, setUniqueStatuses] = useState([]);

  const fetchUsers = useCallback(async ({ showLoading = false } = {}) => {
    if (showLoading) setLoading(true);
    try {
      const res = await axios.get("/api/admin/users");
      setUsers(res.data);

      // Формируем уникальные значения для фильтров
      const roles = [...new Set(res.data.map((user) => user.role))];
      setUniqueRoles(roles);

      const plans = [
        ...new Set(
          res.data.map((user) =>
            isAdminOrManagerRole(user.role)
              ? "Unlimited"
              : user.planName || "Basic",
          ),
        ),
      ];
      setUniquePlans(plans);

      const statuses = [
        ...new Set(res.data.map((user) => user.status || "Active")),
      ];
      setUniqueStatuses(statuses);
    } catch (error) {
      console.error("Error fetching users:", error);
      setErrorMessage("Failed to fetch users.");
    } finally {
      if (showLoading) setLoading(false);
      initialLoadDone.current = true;
    }
  }, [setErrorMessage]);

  useEffect(() => {
    fetchUsers({ showLoading: true });
    // Загружаем email аккаунты для отправки писем
    axios
      .get("/api/mail/accounts", { withCredentials: true })
      .then((res) => {
        setEmailAccounts(res.data || []);
        if (res.data && res.data.length > 0) {
          setMailFrom(res.data[0].email);
        }
      })
      .catch((err) => console.error("Error fetching email accounts:", err));
  }, [fetchUsers]);

  useEffect(() => {
    if (!initialLoadDone.current || userStatuses.size === 0) return;

    setUsers((prev) =>
      prev.map((user) =>
        userStatuses.has(user._id)
          ? { ...user, isOnline: userStatuses.get(user._id) }
          : user
      )
    );
  }, [userStatuses]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchUsers({ showLoading: false });
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchUsers]);

  // ??????? ????????? ????????
  const handleFilterChange = (e, field) => {
    setFilters({ ...filters, [field]: e.target.value });
    setPage(0);
  };

  // ?????????? ????????????? ?? ????????? ?????????
  const filteredUsers = users.filter((user) => {
    const createdAtStr = new Date(user.createdAt).toLocaleString();
    const userPlan = isAdminOrManagerRole(user.role)
      ? "Unlimited"
      : user.planName || "Basic";

    return (
      user.login.toLowerCase().includes(filters.login.toLowerCase()) &&
      user.email.toLowerCase().includes(filters.email.toLowerCase()) &&
      (filters.role === "" ||
        normalizeRole(user.role) === normalizeRole(filters.role)) &&
      (filters.plan === "" || userPlan === filters.plan) &&
      (filters.status === "" || (user.status || "Active") === filters.status) &&
      createdAtStr.toLowerCase().includes(filters.createdAt.toLowerCase())
    );
  });

  // ?????? ??? ??????? ????????
  const currentRows = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // ????????? ?????????
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ???????? ???? ????????
  const handleMenuOpen = (event, user) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const openSwitchUserDialog = (user) => {
    setSelectedUser(user);
    handleMenuClose();
    setSwitchUserDialogOpen(true);
  };

  const handleUserSwitch = (newUser) => {
    if (typeof onUserChange === "function") {
      onUserChange(newUser);
    }
    setSwitchUserDialogOpen(false);
  };

  // ??????? ????????
  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword("");
    setConfirmPassword("");
    setPasswordDialogOpen(true);
    handleMenuClose();
  };

  const closePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setSelectedUser(null);
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }
    try {
      await axios.put(`/api/admin/users/${selectedUser._id}/password`, {
        newPassword,
      });
      setSuccessMessage("Password changed successfully.");
      closePasswordDialog();
      fetchUsers();
    } catch (error) {
      console.error("Error changing password:", error);
      setErrorMessage("Failed to change password.");
    }
  };

  const openRoleDialog = (user) => {
    setSelectedUser(user);
    setSelectedRole(user.role);
    setRoleDialogOpen(true);
    handleMenuClose();
  };

  const closeRoleDialog = () => {
    setRoleDialogOpen(false);
    setSelectedUser(null);
  };

  const handleChangeRole = async () => {
    if (!selectedRole) {
      setErrorMessage("Please select a role.");
      return;
    }
    try {
      await axios.put(`/api/admin/users/${selectedUser._id}/role`, {
        role: selectedRole,
      });
      setSuccessMessage("Role changed successfully.");
      closeRoleDialog();
      fetchUsers();
    } catch (error) {
      console.error("Error changing role:", error);
      setErrorMessage("Failed to change role.");
    }
  };

  const openStatusDialog = (user) => {
    setSelectedUser(user);
    setSelectedStatus(user.status || "Active");
    setStatusDialogOpen(true);
    handleMenuClose();
  };

  const closeStatusDialog = () => {
    setStatusDialogOpen(false);
    setSelectedUser(null);
  };

  const handleChangeStatus = async () => {
    try {
      await axios.put(`/api/admin/users/${selectedUser._id}`, {
        status: selectedStatus,
      });
      setSuccessMessage(
        `Status changed to ${selectedStatus} for user ${selectedUser.login}`,
      );
      closeStatusDialog();
      fetchUsers();
    } catch (error) {
      console.error("Error changing status:", error);
      setErrorMessage("Failed to change status.");
    }
  };

  const openPlanDialog = (user) => {
    setSelectedUser(user);
    setNewPlan(user.planName || "Basic");
    setPlanDialogOpen(true);
    handleMenuClose();
  };

  const closePlanDialog = () => {
    setPlanDialogOpen(false);
    setSelectedUser(null);
  };

  const handleChangePlan = async () => {
    const planObj = availablePlans.find((plan) => plan.name === newPlan);
    if (!planObj) {
      setErrorMessage("Please select a valid plan.");
      return;
    }

    try {
      await axios.put(`/api/admin/users/${selectedUser._id}`, {
        planName: planObj.name,
        planLimit: planObj.limit,
      });

      setSuccessMessage(`Plan changed to ${planObj.name} successfully.`);
      closePlanDialog();
      fetchUsers();
    } catch (error) {
      console.error("Error changing plan:", error);

      // *** ДОБАВЛЕНО: Обработка конфликта с подпиской ***
      if (
        error.response?.status === 409 &&
        error.response?.data?.error === "SUBSCRIPTION_CONFLICT"
      ) {
        const conflictData = error.response.data;

        // Показываем диалог подтверждения
        const confirmMessage =
          `⚠️ Subscription Conflict Detected!\n\n` +
          `User ${selectedUser.login} has an active ${conflictData.subscriptionInfo.planName} subscription (${conflictData.subscriptionInfo.status}).\n\n` +
          `Changing the plan will:\n` +
          `• Cancel their current subscription at period end\n` +
          `• Apply the new admin-assigned plan\n` +
          `• Reset their email usage counter\n\n` +
          `Do you want to proceed with this change?`;

        if (window.confirm(confirmMessage)) {
          try {
            // Повторяем запрос с флагом принудительного обновления
            await axios.put(
              `/api/admin/users/${selectedUser._id}?forceUpdate=true`,
              {
                planName: planObj.name,
                planLimit: planObj.limit,
              },
            );

            setSuccessMessage(
              `Plan changed to ${planObj.name} successfully. User's subscription was canceled.`,
            );
            closePlanDialog();
            fetchUsers();
          } catch (forceError) {
            console.error("Error forcing plan change:", forceError);
            setErrorMessage(
              "Failed to change plan even with force update. Please contact support.",
            );
          }
        }
      } else {
        setErrorMessage(
          error.response?.data?.error || "Failed to change plan.",
        );
      }
    }
  };

  const checkLoginUnique = async () => {
    if (!newUser.login) return;
    try {
      const res = await axios.get(
        `/api/auth/check-duplicate-login?login=${encodeURIComponent(newUser.login)}`,
      );
      if (res.data.exists) {
        setLoginError("A user with this login already exists.");
        return true;
      } else {
        setLoginError("");
        return false;
      }
    } catch (error) {
      console.error("Error checking login uniqueness:", error);
      return false;
    }
  };

  const checkEmailUnique = async () => {
    if (!newUser.email) return;
    try {
      const res = await axios.get(
        `/api/auth/check-duplicate-email?email=${encodeURIComponent(newUser.email)}`,
      );
      if (res.data.exists) {
        setEmailError("A user with this email already exists.");
        return true;
      } else {
        setEmailError("");
        return false;
      }
    } catch (error) {
      console.error("Error checking email uniqueness:", error);
      return false;
    }
  };

  const handleAddUser = async () => {
    if (newUser.password !== newUser.confirmPassword) {
      setErrorMessage("Passwords do not match");
      return;
    }

    // Проверяем уникальность логина и email
    const loginExists = await checkLoginUnique();
    const emailExists = await checkEmailUnique();

    if (loginExists || emailExists) {
      return;
    }

    try {
      await axios.post("/api/admin/users", {
        login: newUser.login,
        email: newUser.email,
        password: newUser.password,
        role: newUser.role,
        planName: newUser.plan,
      });
      setSuccessMessage("User created successfully");
      setAddUserDialogOpen(false);
      setNewUser({
        login: "",
        email: "",
        password: "",
        confirmPassword: "",
        role: "",
        plan: "",
      });
      fetchUsers();
    } catch (error) {
      console.error("Error creating user:", error);
      setErrorMessage("Failed to create user. Please try again.");
    }
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedUser(null);
  };

  const handleDeleteUser = async () => {
    try {
      await axios.delete(`/api/admin/users/${selectedUser._id}`);
      setSuccessMessage(
        `User ${selectedUser.login} has been successfully deleted`,
      );
      closeDeleteDialog();
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      setErrorMessage("Failed to delete user.");
    }
  };

  // Функции для отправки email
  const handleSendMailOpen = () => {
    if (!selectedUser) return;
    setMailRecipient(selectedUser.email);
    setMailSubject(`Message for ${selectedUser.login}`);
    setMailBody("");
    setAttachedFiles([]);
    setSendingMail(false); // Сбрасываем состояние загрузки
    setSendMailDialogOpen(true);
    handleMenuClose();
  };

  const handleSendMailClose = () => {
    setSendMailDialogOpen(false);
  };

  const handleSendMail = async () => {
    // Защита от повторных нажатий
    if (sendingMail) {
      return;
    }

    if (!mailFrom || !mailRecipient || !mailSubject) {
      setErrorMessage("Please fill in all required fields.");
      return;
    }

    const accountId = emailAccounts.length > 0 ? emailAccounts[0]._id : null;
    if (!accountId) {
      setErrorMessage("No sending email account available.");
      return;
    }

    setSendingMail(true);
    try {
      const data = new FormData();
      data.append("to", mailRecipient);
      data.append("subject", mailSubject);
      data.append("htmlContent", mailBody);
      attachedFiles.forEach((file) => {
        data.append("attachments", file);
      });

      await axios.post(`/api/mail/messages/${accountId}/send`, data, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSuccessMessage("Email sent successfully!");
      setSendMailDialogOpen(false);
    } catch (error) {
      console.error("Error sending email:", error);
      setErrorMessage("Failed to send email.");
    } finally {
      setSendingMail(false);
    }
  };

  // Функции для работы с файлами
  const handleSendMailFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachedFiles((prev) => [...prev, ...files]);
  };

  const handleRemoveSendMailFile = (index) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    setAttachedFiles((prev) => [...prev, ...files]);
  };

  return (
    <PageShell>
      <PageSnackbars {...snackbarProps} />

      <PageHeader
        title="User Management"
        subtitle="Manage accounts, roles, plans and permissions"
        sx={{ mb: 2 }}
        actions={
          <Button
            startIcon={<AddIcon sx={{ fontSize: "1.125rem !important" }} />}
            onClick={() => setAddUserDialogOpen(true)}
          >
            Add User
          </Button>
        }
      />

      {loading && users.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: acme.colors.foreground }} size={32} />
        </Box>
      ) : (
        <Card sx={{ ...acmePageSectionCardSx, boxShadow: "none", mt: 0 }}>
          <CardContent sx={acmeBillingSectionContentSx}>
            <PageBlockTitle gutterBottom>User Management</PageBlockTitle>
        <Table>
            <Table.Head>
              <TableRow>
                <Table.Cell><strong>Avatar</strong></Table.Cell>
                <Table.Cell><strong>Login</strong></Table.Cell>
                <Table.Cell><strong>Email</strong></Table.Cell>
                <Table.Cell><strong>Role</strong></Table.Cell>
                <Table.Cell><strong>Plan</strong></Table.Cell>
                <Table.Cell><strong>Status</strong></Table.Cell>
                <Table.Cell><strong>All Emails</strong></Table.Cell>
                <Table.Cell><strong>All SMS</strong></Table.Cell>
                <Table.Cell><strong>Created At</strong></Table.Cell>
                <Table.Cell><strong>Actions</strong></Table.Cell>
              </TableRow>
              <TableRow>
                <Table.Cell />
                <Table.Cell>
                  <Input
                    value={filters.login}
                    onChange={(e) => handleFilterChange(e, "login")}
                    placeholder="Filter Login"
                  />
                </Table.Cell>
                <Table.Cell>
                  <Input
                    value={filters.email}
                    onChange={(e) => handleFilterChange(e, "email")}
                    placeholder="Filter Email"
                  />
                </Table.Cell>
                <Table.Cell>
                  <Select
                    value={filters.role}
                    onChange={(e) => handleFilterChange(e, "role")}
                    displayEmpty
                    renderValue={(value) => (value === "" ? "Role" : value)}
                  >
                    <Select.Item value="">All</Select.Item>
                    {uniqueRoles.map((role) => (
                      <Select.Item key={role} value={role}>{role}</Select.Item>
                    ))}
                  </Select>
                </Table.Cell>
                <Table.Cell>
                  <Select
                    value={filters.plan}
                    onChange={(e) => handleFilterChange(e, "plan")}
                    displayEmpty
                    renderValue={(value) => (value === "" ? "Plan" : value)}
                  >
                    <Select.Item value="">All</Select.Item>
                    {uniquePlans.map((plan) => (
                      <Select.Item key={plan} value={plan}>{plan}</Select.Item>
                    ))}
                  </Select>
                </Table.Cell>
                <Table.Cell>
                  <Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange(e, "status")}
                    displayEmpty
                    renderValue={(value) => (value === "" ? "Status" : value)}
                  >
                    <Select.Item value="">All</Select.Item>
                    {uniqueStatuses.map((status) => (
                      <Select.Item key={status} value={status}>{status}</Select.Item>
                    ))}
                  </Select>
                </Table.Cell>
                <Table.Cell />
                <Table.Cell />
                <Table.Cell>
                  <Input
                    value={filters.createdAt}
                    onChange={(e) => handleFilterChange(e, "createdAt")}
                    placeholder="Filter Date"
                  />
                </Table.Cell>
                <Table.Cell />
              </TableRow>
            </Table.Head>
            <Table.Body>
              {currentRows.map((user) => (
                <TableRow key={user._id}>
                  <Table.Cell>
                    <AvatarWithStatus user={user} sx={{ width: 40, height: 40 }} />
                  </Table.Cell>
                  <Table.Cell>{user.login}</Table.Cell>
                  <Table.Cell>{user.email}</Table.Cell>
                  <Table.Cell>{user.role}</Table.Cell>
                  <Table.Cell>
                    {isAdminOrManagerRole(user.role) ? "Unlimited" : user.planName || "Basic"}
                    {user.stripeSubscriptionId &&
                      user.subscriptionStatus &&
                      ["active", "trialing", "past_due"].includes(user.subscriptionStatus) && (
                        <Chip label="Subscription" sx={{ ml: 1 }} />
                      )}
                  </Table.Cell>
                  <Table.Cell>{user.status || "Active"}</Table.Cell>
                  <Table.Cell>{user.allSentMessages ?? 0}</Table.Cell>
                  <Table.Cell>{user.allSentSms ?? 0}</Table.Cell>
                  <Table.Cell>{new Date(user.createdAt).toLocaleString()}</Table.Cell>
                  <Table.Cell onClick={stopTableRowClick}>
                    <TableActionsMenuButton
                      ariaLabel="User actions"
                      onClick={(e) => handleMenuOpen(e, user)}
                    />
                  </Table.Cell>
                </TableRow>
              ))}
            </Table.Body>
            <Table.Footer>
              <TableRow>
                <TablePagination
                  count={filteredUsers.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                  labelRowsPerPage="Rows per page"
                />
              </TableRow>
            </Table.Footer>
        </Table>
          </CardContent>
        </Card>
      )}

      <TableActionsMenu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        <Menu.Item onClick={() => openSwitchUserDialog(selectedUser)}>
          <SwitchAccountIcon fontSize="small" sx={{ mr: 1 }} />
          Switch to User
        </Menu.Item>
        <Menu.Item onClick={() => openPasswordDialog(selectedUser)}>Change Password</Menu.Item>
        <Menu.Item onClick={() => openRoleDialog(selectedUser)}>Change Role</Menu.Item>
        <Menu.Item onClick={() => openStatusDialog(selectedUser)}>Status User</Menu.Item>
        {selectedUser && !isAdminOrManagerRole(selectedUser.role) && (
          <Menu.Item onClick={() => openPlanDialog(selectedUser)}>Change Plan</Menu.Item>
        )}
        <Menu.Item onClick={handleSendMailOpen}>Send mail</Menu.Item>
        <Menu.Item
          onClick={() => openDeleteDialog(selectedUser)}
          sx={destructiveMenuItemSx}
        >
          Delete User
        </Menu.Item>
      </TableActionsMenu>

      <Dialog open={passwordDialogOpen} onClose={closePasswordDialog}>
        <Dialog.Title>Change Password for {selectedUser?.login}</Dialog.Title>
        <Dialog.Body>
          <Input label="New Password" type="password" margin="normal" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} inputProps={{ autocomplete: "new-password" }} />
          <Input label="Confirm Password" type="password" margin="normal" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} inputProps={{ autocomplete: "new-password" }} />
        </Dialog.Body>
        <Dialog.Footer>
          <Button variant="outline" onClick={closePasswordDialog}>Cancel</Button>
          <Button onClick={handleChangePassword}>Save</Button>
        </Dialog.Footer>
      </Dialog>

      <Dialog open={roleDialogOpen} onClose={closeRoleDialog}>
        <Dialog.Title>Change Role for {selectedUser?.login}</Dialog.Title>
        <Dialog.Body>
          <Select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)} sx={{ mt: 1 }}>
            <Select.Item value={ROLE_ADMIN}>Admin</Select.Item>
            <Select.Item value={ROLE_USER}>User</Select.Item>
            <Select.Item value={ROLE_MANAGER}>Manager</Select.Item>
            <Select.Item value={ROLE_SUPPORT}>Support</Select.Item>
          </Select>
        </Dialog.Body>
        <Dialog.Footer>
          <Button variant="outline" onClick={closeRoleDialog}>Cancel</Button>
          <Button onClick={handleChangeRole}>Save</Button>
        </Dialog.Footer>
      </Dialog>

      <Dialog open={statusDialogOpen} onClose={closeStatusDialog}>
        <Dialog.Title>Status User for {selectedUser?.login}</Dialog.Title>
        <Dialog.Body>
          <Select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} sx={{ mt: 1 }}>
            <Select.Item value="Active">Active</Select.Item>
            <Select.Item value="Moderation">Moderation</Select.Item>
            <Select.Item value="Block">Block</Select.Item>
          </Select>
        </Dialog.Body>
        <Dialog.Footer>
          <Button variant="outline" onClick={closeStatusDialog}>Cancel</Button>
          <Button onClick={handleChangeStatus}>Save</Button>
        </Dialog.Footer>
      </Dialog>

      <Dialog open={planDialogOpen} onClose={closePlanDialog}>
        <Dialog.Title>Change Plan for {selectedUser?.login}</Dialog.Title>
        <Dialog.Body>
          <Select value={newPlan} onChange={(e) => setNewPlan(e.target.value)} sx={{ mt: 1 }}>
            {availablePlans.map((plan) => (
              <Select.Item key={plan.name} value={plan.name}>
                {plan.name} (Limit: {plan.limit})
              </Select.Item>
            ))}
          </Select>
        </Dialog.Body>
        <Dialog.Footer>
          <Button variant="outline" onClick={closePlanDialog}>Cancel</Button>
          <Button onClick={handleChangePlan}>Save</Button>
        </Dialog.Footer>
      </Dialog>

      <Dialog open={addUserDialogOpen} onClose={() => setAddUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <Dialog.Title>Add New User</Dialog.Title>
        <Dialog.Body>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Input label="Login" value={newUser.login} onChange={(e) => setNewUser({ ...newUser, login: e.target.value })} onBlur={checkLoginUnique} error={!!loginError} helperText={loginError} required />
            <Input label="Email" type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} onBlur={checkEmailUnique} error={!!emailError} helperText={emailError} required />
            <Input label="Password" type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} required inputProps={{ autocomplete: "new-password" }} />
            <Input label="Confirm Password" type="password" value={newUser.confirmPassword} onChange={(e) => setNewUser({ ...newUser, confirmPassword: e.target.value })} required inputProps={{ autocomplete: "new-password" }} />
            <Select label="Role" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} required>
              <Select.Item value={ROLE_ADMIN}>Admin</Select.Item>
              <Select.Item value={ROLE_USER}>User</Select.Item>
              <Select.Item value={ROLE_MANAGER}>Manager</Select.Item>
              <Select.Item value={ROLE_SUPPORT}>Support</Select.Item>
            </Select>
            <Select label="Plan" value={newUser.plan} onChange={(e) => setNewUser({ ...newUser, plan: e.target.value })} required>
              {availablePlans.map((plan) => (
                <Select.Item key={plan.name} value={plan.name}>
                  {plan.name} (Limit: {plan.limit})
                </Select.Item>
              ))}
            </Select>
          </Box>
        </Dialog.Body>
        <Dialog.Footer>
          <Button variant="outline" onClick={() => setAddUserDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddUser}>Create User</Button>
        </Dialog.Footer>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <Dialog.Title>Delete User</Dialog.Title>
        <Dialog.Body>
          <Typography sx={{ color: acme.colors.foreground }}>
            Are you sure you want to delete user <strong>{selectedUser?.login}</strong>?
          </Typography>
          <Typography sx={{ mt: 2, fontSize: '0.875rem', color: acme.colors.mutedForegroundSolid }}>
            This action cannot be undone. All user data will be permanently deleted.
          </Typography>
        </Dialog.Body>
        <Dialog.Footer>
          <Button variant="outline" onClick={closeDeleteDialog}>Cancel</Button>
          <Button variant="destructive" onClick={handleDeleteUser}>Delete</Button>
        </Dialog.Footer>
      </Dialog>

      <Dialog open={sendMailDialogOpen} onClose={handleSendMailClose} fullWidth maxWidth="sm">
        <Dialog.Title>Send Mail</Dialog.Title>
        <Dialog.Body sx={{ display: "flex", flexDirection: "column" }}>
          <Select label="From" labelId="mail-from-label" value={mailFrom} onChange={(e) => setMailFrom(e.target.value)}>
            {emailAccounts.map((acc) => (
              <Select.Item key={acc._id} value={acc.email}>{acc.email}</Select.Item>
            ))}
          </Select>
          <Input label="To" margin="normal" value={mailRecipient} onChange={(e) => setMailRecipient(e.target.value)} placeholder="Enter recipient email" />
          <Input label="Subject" margin="normal" value={mailSubject} onChange={(e) => setMailSubject(e.target.value)} placeholder="Enter subject" />
          <Input label="Email Content" margin="normal" multiline minRows={6} value={mailBody} onChange={(e) => setMailBody(e.target.value)} placeholder="Enter email content here..." />
          <Box sx={{ mt: 2 }}>
            <Box component="label" onDrop={handleDrop} onDragOver={handleDragOver} sx={{ width: "100%", boxSizing: "border-box", border: `2px dashed ${acme.colors.border}`, borderRadius: acme.radius.md, p: 2, textAlign: "center", cursor: "pointer", display: "block", transition: 'border-color 0.15s', '&:hover': { borderColor: acme.colors.mutedForegroundSolid } }}>
              <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid }}>
                Drag and drop files here, or click to attach files.
              </Typography>
              <input type="file" hidden multiple onChange={handleSendMailFileChange} />
            </Box>
            <Typography variant="caption" sx={{ display: "block", mt: 1, color: acme.colors.mutedForegroundSolid }}>
              Note: The email will be sent using our default template.
            </Typography>
          </Box>
          {attachedFiles.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
              {attachedFiles.map((file, index) => (
                <Box key={index} sx={{ display: "flex", alignItems: "center", border: `1px solid ${acme.colors.border}`, borderRadius: acme.radius.sm, p: 1 }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>{file.name}</Typography>
                  <MuiIconButton size="small" onClick={() => handleRemoveSendMailFile(index)}>
                    <DeleteIcon fontSize="small" />
                  </MuiIconButton>
                </Box>
              ))}
            </Box>
          )}
        </Dialog.Body>
        <Dialog.Footer>
          <Button variant="outline" onClick={handleSendMailClose} disabled={sendingMail}>Cancel</Button>
          <Button onClick={handleSendMail} disabled={sendingMail}>{sendingMail ? "Sending..." : "Send"}</Button>
        </Dialog.Footer>
      </Dialog>

      <Dialog open={switchUserDialogOpen} onClose={() => setSwitchUserDialogOpen(false)} maxWidth="md" fullWidth>
        <Dialog.Title>Switch User Account</Dialog.Title>
        <Dialog.Body>
          <UserSelector currentUser={currentUser} initialUserId={selectedUser?._id} onUserSwitch={handleUserSwitch} onClose={() => setSwitchUserDialogOpen(false)} />
        </Dialog.Body>
      </Dialog>
    </PageShell>
  );
};

export default AdminDashboard;
